import { BaseCarousel } from 'components/Carousels/BaseCarousel'
import { MediaImageStandard, Typography, LinkButton } from 'components/Contentful'
import { Box } from '@achieve/ascend'
import { BREAKPOINTS } from 'constants/viewport'
import useViewportSize from 'hooks/useViewportSize'
import { _get } from 'utils/shared'
import classNames from 'classnames'
import { useBot } from 'hooks/useBot'

const FONT_MAP = {
  BUGLINE: {
    [BREAKPOINTS.xxs]: { variant: 'bodyLg', weight: 'regular' },
    [BREAKPOINTS.xs]: { variant: 'bodyLg', weight: 'regular' },
    [BREAKPOINTS.sm]: { variant: 'bodyLg', weight: 'regular' },
    [BREAKPOINTS.md]: { variant: 'headingMd', weight: 'medium' },
    [BREAKPOINTS.lg]: { variant: 'headingMd', weight: 'medium' },
    [BREAKPOINTS.xl]: { variant: 'headingMd', weight: 'medium' },
    [BREAKPOINTS.xxl]: { variant: 'headingMd', weight: 'medium' },
  },
  TITLE: {
    [BREAKPOINTS.xxs]: { variant: 'headingLg', weight: 'bold' },
    [BREAKPOINTS.xs]: { variant: 'headingLg', weight: 'bold' },
    [BREAKPOINTS.sm]: { variant: 'headingLg', weight: 'bold' },
    [BREAKPOINTS.md]: { variant: 'displayXl', weight: 'bold' },
    [BREAKPOINTS.lg]: { variant: 'displayXl', weight: 'bold' },
    [BREAKPOINTS.xl]: { variant: 'displayXl', weight: 'bold' },
    [BREAKPOINTS.xxl]: { variant: 'displayXl', weight: 'bold' },
  },
  BUTTON: {
    [BREAKPOINTS.xxs]: { variant: 'bodySm', weight: 'bold' },
    [BREAKPOINTS.xs]: { variant: 'bodySm', weight: 'bold' },
    [BREAKPOINTS.sm]: { variant: 'bodySm', weight: 'bold' },
    [BREAKPOINTS.md]: { variant: 'bodyLg', weight: 'bold' },
    [BREAKPOINTS.lg]: { variant: 'bodyLg', weight: 'bold' },
    [BREAKPOINTS.xl]: { variant: 'bodyLg', weight: 'bold' },
    [BREAKPOINTS.xxl]: { variant: 'bodyLg', weight: 'bold' },
  },
}
function Card({ item, selected }) {
  const isBot = useBot()
  const { breakpointLetterSize } = useViewportSize()

  if (!item) return null

  const {
    title,
    bugline,
    backgroundMobile,
    background,
    alt,
    buttonCta,
    priority,
    titleColor,
    buglineColor,
    titlePosition,
    buglinePosition,
    titleShadow,
    buglineShadow,
  } = item
  const DEFAULT_COLOR = 'font-white'
  const DEFAULT_SHADOW = 'shadow-none'
  const finalTitleColor = titleColor ? `font-${titleColor.toLowerCase()}` : DEFAULT_COLOR
  const finalBuglineColor = buglineColor ? `font-${buglineColor.toLowerCase()}` : DEFAULT_COLOR
  const finalTitleShadow = titleShadow ? `shadow-${titleShadow.toLowerCase()}` : DEFAULT_SHADOW
  const finalBuglineShadow = buglineShadow
    ? `shadow-${buglineShadow.toLowerCase()}`
    : DEFAULT_SHADOW

  return (
    <Box
      className={[
        'flex h-full justify-center transition-opacity duration-1000 ease-[ease] bg-blue-50',
        selected && 'transition-opacity duration-1000 ease-[ease]',
      ]}
      elevation={selected ? 10 : 1}
    >
      <div
        className={
          'w-full h-full relative z-0 flex flex-col items-center rounded-lg overflow-hidden'
        }
      >
        <div className="absolute inset-0 z-0">
          <MediaImageStandard
            content={
              [BREAKPOINTS.xxs, BREAKPOINTS.xs, BREAKPOINTS.sm].includes(breakpointLetterSize)
                ? backgroundMobile
                : background
            }
            alt={alt}
            layout="fill"
            objectFit="cover"
            objectPosition="top"
            fetchPriority="high"
            preload={priority}
          />
        </div>
        <Box
          className={
            'relative z-1 h-full max-w-[295px] flex items-center justify-center flex-col text-center md:max-w-[545px] lg:max-w-[1007px] lg:w-[1007px]'
          }
          component="div"
        >
          <Typography
            content={bugline}
            variant={FONT_MAP.BUGLINE[breakpointLetterSize].variant}
            fontWeight={FONT_MAP.BUGLINE[breakpointLetterSize].weight}
            className={classNames(
              'pb-3',
              finalBuglineColor === 'font-white' && 'text-white',
              finalBuglineColor === 'font-black' && 'text-black',
              finalBuglineColor === 'font-blue' && 'text-blue-500',
              !finalBuglineColor && 'text-white',
              finalBuglineShadow === 'shadow-none' && 'shadow-none',
              finalBuglineShadow === 'shadow-min' &&
                '[text-shadow:0_2px_9px_rgb(16_24_40/9%),0_2px_3px_rgb(16_24_40/3%)]',
              !finalBuglineShadow && 'shadow-none',
              //buglinePositions
              //buglinePosition Right
              buglinePosition &&
                buglinePosition.toLowerCase() === 'right' &&
                'w-[560px] text-left max-w-none ml-[440px] 2xl:ml-[640px]',
              //buglinePosition Left
              buglinePosition &&
                buglinePosition.toLowerCase() === 'left' &&
                'w-full text-left max-w-none ml-[120px] 2xl:ml-[240px]'
            )}
            component={selected ? 'h1' : 'p'}
          />
          <Typography
            content={title}
            variant={FONT_MAP.TITLE[breakpointLetterSize].variant}
            fontWeight={FONT_MAP.TITLE[breakpointLetterSize].weight}
            className={classNames(
              'md:max-w-[550px]',
              finalTitleShadow === 'shadow-none' && 'shadow-none',
              finalTitleShadow === 'shadow-min' &&
                '[text-shadow:0_2px_9px_rgb(16_24_40/9%),0_2px_3px_rgb(16_24_40/3%)]',
              !finalTitleShadow && 'shadow-none',
              finalTitleColor === 'font-white' && 'text-white',
              finalTitleColor === 'font-black' && 'text-black',
              finalTitleColor === 'font-blue' && 'text-blue-500',
              !finalTitleColor && 'text-white',
              //titlePositions
              //titlePosition Left
              titlePosition &&
                titlePosition.toLowerCase() === 'left' &&
                'w-full text-left max-w-none ml-[120px] 2xl:ml-[240px]',
              //titlePosition Right
              titlePosition &&
                titlePosition.toLowerCase() === 'right' &&
                'w-[560px] text-left max-w-none ml-[440px] 2xl:ml-[640px]'
            )}
          />
          {buttonCta && (
            <span
              className={classNames(
                titlePosition &&
                  titlePosition.toLowerCase() === 'left' &&
                  'w-full text-left max-w-none ml-[120px] 2xl:ml-[240px]',
                //titlePosition Right
                titlePosition &&
                  titlePosition.toLowerCase() === 'right' &&
                  'w-[560px] text-left max-w-none ml-[440px] 2xl:ml-[640px]'
              )}
            >
              <LinkButton
                track={{
                  ...{
                    list_name: 'Hero Carousel Event',
                    hero_carousel_section: 'Hero CTA Clicked',
                    click_type: 'Button Click',
                    click_id: _get(buttonCta, 'linkText.content.0.content.0.value', ''),
                    click_url: _get(buttonCta, 'linkHref', ''),
                    click_text: _get(buttonCta, 'linkText.content.0.content.0.value', ''),
                    click_position: '0',
                    nav_link_section: 'Hero Carousel',
                    track_event: 'internal_campaign_click',
                    event_action: 'ui_click',
                    event_type: 'ui_click',
                    isBot,
                  },
                  ...buttonCta?.fields?.event,
                }}
                content={buttonCta}
                typographicOptions={{
                  variant: FONT_MAP.BUTTON[breakpointLetterSize].variant,
                  fontWeight: FONT_MAP.BUTTON[breakpointLetterSize].weight,
                }}
                size="large"
                classes={{
                  root: 'mt-12',
                }}
              />
            </span>
          )}
        </Box>
      </div>
    </Box>
  )
}

function HeroCarousel({ slides }) {
  return (
    <Box data-testid="home-hero-container">
      <BaseCarousel
        items={slides ?? []}
        renderItem={(item, index, isActive) => <Card item={item} selected={isActive} />}
        autoPlay
        loop
        classNameContainer="h-[485px] p-0"
        classNameTrack="h-full"
        classNameSlide="w-full h-full"
        classNameIndicators="absolute mt-[-56px] lg:mt-[-33px] bg-transparent"
        classNameControls={'h-10 w-10 [&_.control-left]:left-[calc(50%-500px)]!'}
      />
    </Box>
  )
}

export { HeroCarousel }
